import IdListDTO from 'models/dto/IdListDTO';
import {
  mapFromProviderItem,
  mapFromProviderItemMetadata,
  mapFromProviderItemMetadatas,
  mapFromProviderPageContent,
} from 'mappings/documentProviderMapping';
import { mapFromResourceLink } from 'mappings/linkMapping';
import { ProviderItem, ProviderItemMetadata, ProviderPageContent } from 'models/documentConnection';
import {
  ProviderCreateFolderDTO,
  ProviderItemDTO,
  ProviderItemMetadataDTO,
  ProviderPageContentDTO,
  ProviderUploadResultDTO,
} from 'models/dto/documentProviderDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import ResourceLink from 'models/resourceLink';
import AppError from 'utils/appError';
import http from './httpService';

/**
 * Content operations for library items held by a document provider. Everything here is
 * brokered by the API with the tenant's stored credentials; SharePoint and virtual items keep
 * their own paths (direct Graph and the content service respectively).
 */

export async function apiGetLinkContentFile(linkId: number, accessToken: string): Promise<Blob> {
  try {
    const ar = await http.get<Blob>(`/links/${linkId}/content/file`, {
      ...http.getRequestConfig(accessToken),
      responseType: 'blob',
    });

    return ar.data;
  } catch (err) {
    // the error body arrives as a Blob too, and would otherwise lose its DOCPROV code
    throw await AppError.fromBlobApiError(err);
  }
}

/**
 * The bytes to show in the browser. The same as the download for most items; a Google-native
 * document comes back as PDF here, where the download hands out the Office export.
 */
export async function apiGetLinkContentPreview(linkId: number, accessToken: string): Promise<Blob> {
  try {
    const ar = await http.get<Blob>(`/links/${linkId}/content/preview`, {
      ...http.getRequestConfig(accessToken),
      responseType: 'blob',
    });

    return ar.data;
  } catch (err) {
    throw await AppError.fromBlobApiError(err);
  }
}

/**
 * A short-lived url of a copy of an Office file, for the Office Online viewer: Microsoft's servers
 * fetch the file themselves, so the API parks it behind a read-only url that expires after a few
 * minutes. Refused by code when the stage has the viewer off (DOCPROV0001), for a file the viewer
 * cannot show (DOCPROV0011) or one above its size limit (DOCPROV0014, the limit in the message).
 */
export async function apiGetLinkContentPreviewUrl(linkId: number, accessToken: string): Promise<string> {
  try {
    const ar = await http.get<{ valueString?: string }>(
      `/links/${linkId}/content/previewurl`,
      http.getRequestConfig(accessToken),
    );

    return ar.data?.valueString ?? '';
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * The provider's own preview page of the item, for an iframe (Google Drive's embed page). The page
 * renders with the browser's own session at the provider, so the API answers it for users who
 * signed in through that provider only.
 */
export async function apiGetLinkEmbedUrl(linkId: number, accessToken: string): Promise<string> {
  try {
    const ar = await http.get<{ valueString?: string }>(`/links/${linkId}/embedurl`, http.getRequestConfig(accessToken));

    return ar.data?.valueString ?? '';
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetLinkContentPage(linkId: number, accessToken: string): Promise<ProviderPageContent> {
  try {
    const ar = await http.get<ProviderPageContentDTO>(
      `/links/${linkId}/content/page`,
      http.getRequestConfig(accessToken),
    );

    return mapFromProviderPageContent(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetLinkWebUrl(linkId: number, accessToken: string): Promise<string | undefined> {
  try {
    const ar = await http.get<{ valueString?: string }>(`/links/${linkId}/weburl`, http.getRequestConfig(accessToken));

    return ar.data?.valueString;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetLinkProviderMetadata(linkId: number, accessToken: string): Promise<ProviderItemMetadata> {
  try {
    const ar = await http.get<ProviderItemMetadataDTO>(
      `/links/${linkId}/metadata`,
      http.getRequestConfig(accessToken),
    );

    return mapFromProviderItemMetadata(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Reads several items of one category in a single call. The SharePoint path batches through
 * Graph in the browser; a provider category has no batch API, so the server fans out and
 * answers per item, with hasError set on the ones it could not read.
 */
export async function apiGetListItemsMetadata(
  listId: number,
  linkIds: number[],
  accessToken: string,
): Promise<ProviderItemMetadata[]> {
  try {
    const ar = await http.post<ProviderItemMetadataDTO[]>(
      `/lists/${listId}/items/metadata`,
      new IdListDTO(linkIds.map((id) => id.toString())),
      http.getRequestConfig(accessToken),
    );

    return mapFromProviderItemMetadatas(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export enum UploadConflictBehavior {
  Rename = 0,
  Replace = 1,
  Fail = 2,
}

export interface IUploadListItemResult {
  item?: ProviderItem;
  link?: ResourceLink;
}

export async function apiUploadListItem(
  listId: number,
  file: File,
  parentFolderId: string | undefined,
  conflictBehavior: UploadConflictBehavior,
  createLink: boolean,
  linkName: string | undefined,
  accessToken: string,
  cache: GlobalDataCache,
  signal?: AbortSignal,
): Promise<IUploadListItemResult> {
  try {
    const form = new FormData();
    form.append('file', file, file.name);
    if (parentFolderId) form.append('parentFolderId', parentFolderId);
    form.append('conflictBehavior', conflictBehavior.toString());
    form.append('createLink', createLink ? 'true' : 'false');
    if (linkName) form.append('linkName', linkName);

    const config = http.getRequestConfig(accessToken);
    // the signal lets the picker's Cancel stop a batch between files, and abort the one in flight
    const ar = await http.post<ProviderUploadResultDTO>(`/lists/${listId}/items/upload`, form, {
      ...config,
      headers: { ...config.headers, 'Content-Type': 'multipart/form-data' },
      signal,
    });

    return {
      item: ar.data?.item ? mapFromProviderItem(ar.data.item) : undefined,
      link: ar.data?.link ? mapFromResourceLink(ar.data.link, cache) : undefined,
    };
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCreateListFolder(
  listId: number,
  parentFolderId: string | undefined,
  name: string,
  accessToken: string,
): Promise<ProviderItem> {
  try {
    const dto = new ProviderCreateFolderDTO();
    dto.parentFolderId = parentFolderId;
    dto.name = name;

    const ar = await http.post<ProviderItemDTO>(`/lists/${listId}/folders`, dto, http.getRequestConfig(accessToken));

    return mapFromProviderItem(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
